import { create } from "zustand";
import api, { isAbortError } from "../../utils/api";
import { getError } from "../../utils/getError";

export interface CredentialField {
  field: string;
  label: string;
  secret: boolean;
}

export interface Integrator {
  id: string;
  name: string;
  active: boolean;
  adapter: string | null;
  auth_strategy: string | null;
  protocol: string | null;
  default_endpoint: string | null;
  credentials_schema: CredentialField[];
  createdAt?: { _seconds: number; _nanoseconds: number };
  updatedAt?: { _seconds: number; _nanoseconds: number };
}

export interface CreateIntegratorData {
  name: string;
  active?: boolean;
  adapter?: string | null;
  auth_strategy?: string | null;
  protocol?: string | null;
  default_endpoint?: string | null;
  credentials_schema?: CredentialField[];
}

interface IntegratorsMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

interface IntegratorsStore {
  integrators: Integrator[];
  loading: boolean;
  error: string | null;
  meta?: IntegratorsMeta;
  // Contador independiente para el badge del menú (no toca la lista de la sección)
  totalCount: number;
  countLoading: boolean;
  fetchIntegrators: (params?: {
    limit?: number;
    page?: number;
    name?: string;
    protocol?: string;
    active?: string;
  }) => Promise<void>;
  fetchIntegratorsCount: () => Promise<void>;
  fetchIntegrator: (id: string) => Promise<Integrator>;
  createIntegrator: (data: CreateIntegratorData) => Promise<Integrator>;
  updateIntegrator: (
    id: string,
    data: Partial<CreateIntegratorData>
  ) => Promise<Integrator>;
  deleteIntegrator: (id: string) => Promise<void>;
}

export const useIntegratorsStore = create<IntegratorsStore>((set, get) => ({
  integrators: [],
  loading: false,
  error: null,
  meta: undefined,
  totalCount: 0,
  countLoading: false,

  fetchIntegrators: async (params = {}) => {
    if (get().loading) return;
    set({ loading: true, error: null });
    try {
      const { data } = await api.get("/integratorList", {
        params: {
          limit: params.limit ?? 8,
          page: params.page ?? 1,
          ...params,
        },
      });
      set({ integrators: data.data || [], meta: data.meta, loading: false });
    } catch (err: unknown) {
      if (isAbortError(err)) {
        set({ loading: false });
        return;
      }
      set({ error: getError(err), loading: false });
    }
  },

  fetchIntegratorsCount: async () => {
    if (get().countLoading) return;
    set({ countLoading: true });
    try {
      const { data } = await api.get("/integratorList", {
        params: { limit: 1, page: 1 },
      });
      set({
        totalCount: data.meta?.totalItems ?? 0,
        countLoading: false,
      });
    } catch (err: unknown) {
      if (isAbortError(err)) {
        set({ countLoading: false });
        return;
      }
      set({ countLoading: false });
    }
  },

  fetchIntegrator: async (id: string) => {
    const { data } = await api.get("/integrator", { params: { id } });
    return data.data;
  },

  createIntegrator: async (integratorData) => {
    const { data } = await api.post("/integrator", integratorData);
    return data.data;
  },

  updateIntegrator: async (id: string, integratorData) => {
    const { data } = await api.put("/integrator", integratorData, {
      params: { id },
    });
    return data.data;
  },

  deleteIntegrator: async (id: string) => {
    await api.delete("/integrator", { params: { id } });
  },
}));
